import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { useAuth } from '../../shared/auth/AuthContext';
import { useCompanies } from '../organization/useOrganization';

/* ═══════════════════════════════════════════════════════════════════════════
   Retenção — hooks e tipos do módulo (features/retention)
   ───────────────────────────────────────────────────────────────────────────
   Contratos consumidos (backend já implementado, DTOs reais em
   `backend/apps/api/src/features/retention/presentation/http/dto/retention.dto.ts`):

   - GET  /retention/rules            → PaginatedRetentionRuleResponseDto
   - POST /retention/rules            → RetentionRuleDto (CreateRetentionRuleDto)
   - PATCH/DELETE /retention/rules/:id
   - POST /retention/rules/:id/activate | deactivate → RetentionRuleDto
   - GET  /retention/minimums         → PaginatedRetentionMinimumResponseDto
   - POST /retention/minimums, PATCH/DELETE /retention/minimums/:id
   - GET  /retention/exceptions       → PaginatedRetentionExceptionResponseDto
   - POST /retention/exceptions, PATCH/DELETE /retention/exceptions/:id
   - GET  /retention/monitor          → PaginatedRetentionMonitorResponseDto
         { data: RetentionLogDto[], total, page, limit, totalPages, totais }
   - GET  /retention/audit-log        → PaginatedRetentionAuditLogResponseDto

   Tenancy: tenantId + companyId vêm da sessão (padrão do módulo contábil);
   nunca do corpo da request.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Tipos (espelham os DTOs reais) ── */

export type RetentionRuleStatus = 'ATIVA' | 'INATIVA';
export type RetentionAction = 'RETER_ISS' | 'RETER_IRRF' | 'RETER_ISS_IRRF' | 'NAO_RETER';
export type RetentionLogStatus = 'RETIDO' | 'NAO_RETIDO' | 'ISENTO';
export type RetentionOrigin = 'AUTO' | 'MANUAL';
export type RetentionConditionOperator = '>' | '>=' | '<' | '<=' | '==' | '!=' | 'EM';
export type RetentionConditionField =
  | 'valorBruto'
  | 'tipoServico'
  | 'prestadorCnpj'
  | 'prestadorRazaoSocial'
  | 'uf'
  | 'municipio';

export interface RetentionCondition {
  campo: RetentionConditionField;
  operador: RetentionConditionOperator;
  valor: number | string | string[];
}

export interface RetentionRule {
  id: string;
  prioridade: number;
  condicao: RetentionCondition;
  acao: RetentionAction;
  status: RetentionRuleStatus;
  descricao: string | null;
  retencaoNaFonte: boolean;
  autoLancamento: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RetentionRuleInput {
  prioridade: number;
  condicao: RetentionCondition;
  acao: RetentionAction;
  descricao?: string;
  retencaoNaFonte?: boolean;
  autoLancamento?: boolean;
}

export interface RetentionMinimum {
  id: string;
  tipoServico: string;
  valorMinimo: number;
  vigenteDesde: string;
  updatedAt: string;
}

export interface RetentionMinimumInput {
  tipoServico: string;
  valorMinimo: number;
  vigenteDesde?: string;
}

export interface RetentionException {
  id: string;
  regraId: string | null;
  prestadorCnpj: string;
  prestadorRazaoSocial: string | null;
  motivo: string;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  status: RetentionRuleStatus;
  updatedAt: string;
}

export interface RetentionExceptionInput {
  regraId?: string;
  prestadorCnpj: string;
  prestadorRazaoSocial?: string;
  motivo: string;
  vigenciaInicio: string;
  vigenciaFim?: string;
}

export interface RegraAplicada {
  regraId: string;
  descricao: string | null;
  taxa: number;
  valorCalculado: number;
}

export interface RetentionLog {
  id: string;
  dataHora: string;
  nfseId: string | null;
  prestadorCnpj: string;
  prestadorRazaoSocial: string | null;
  tipoServico: string | null;
  valorBruto: number;
  iss: number;
  irrf: number;
  status: RetentionLogStatus;
  origem: RetentionOrigin;
  motivo: string | null;
  regrasAplicadas: RegraAplicada[];
  criadoPorId: string | null;
  criadoPorNome: string | null;
  createdAt: string;
}

export interface RetentionMonitorTotais {
  valorBruto: number;
  iss: number;
  irrf: number;
  retidos: number;
  naoRetidos: number;
  isentos: number;
}

export interface RetentionMonitor {
  data: RetentionLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  totais: RetentionMonitorTotais;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/* ── Contexto (tenant + primeira empresa da sessão) ── */

export interface ModuleCtx {
  tenantId: string;
  companyId: string;
}

export function useModuleCtx(): ModuleCtx | null {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const { data: companies } = useCompanies(tenantId);
  const companyId = companies?.[0]?.id ?? null;
  if (!tenantId || !companyId) return null;
  return { tenantId, companyId };
}

function ctxParams(ctx: ModuleCtx): Record<string, string> {
  return { tenantId: ctx.tenantId, companyId: ctx.companyId };
}

/* ── Labels / variantes de apresentação ── */

export const RULE_STATUS_LABEL: Record<RetentionRuleStatus, string> = {
  ATIVA: 'Ativa',
  INATIVA: 'Inativa',
};

export const ACTION_LABEL: Record<RetentionAction, string> = {
  RETER_ISS: 'Reter ISS',
  RETER_IRRF: 'Reter IRRF',
  RETER_ISS_IRRF: 'Reter ISS + IRRF',
  NAO_RETER: 'Não Reter',
};

export const LOG_STATUS_LABEL: Record<RetentionLogStatus, string> = {
  RETIDO: 'Retido',
  NAO_RETIDO: 'Não Retido',
  ISENTO: 'Isento / Não Incide',
};

export const ORIGIN_LABEL: Record<RetentionOrigin, string> = {
  AUTO: 'Auto',
  MANUAL: 'Manual',
};

export const CONDITION_FIELD_LABEL: Record<RetentionConditionField, string> = {
  valorBruto: 'Valor Bruto',
  tipoServico: 'Tipo de Serviço',
  prestadorCnpj: 'CNPJ do Prestador',
  prestadorRazaoSocial: 'Razão Social do Prestador',
  uf: 'UF',
  municipio: 'Município',
};

export const CONDITION_OPERATOR_LABEL: Record<RetentionConditionOperator, string> = {
  '>': '>',
  '>=': '≥',
  '<': '<',
  '<=': '≤',
  '==': '=',
  '!=': '≠',
  EM: 'EM',
};

/** Formata a condição "Se" como texto legível (ex.: "Valor Bruto ≥ 100,00"). */
export function formatCondition(cond: RetentionCondition): string {
  const campo = CONDITION_FIELD_LABEL[cond.campo] ?? cond.campo;
  const op = CONDITION_OPERATOR_LABEL[cond.operador] ?? cond.operador;
  if (Array.isArray(cond.valor)) {
    return `${campo} ${op} [${cond.valor.join(', ')}]`;
  }
  if (typeof cond.valor === 'number') {
    return `${campo} ${op} ${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(cond.valor)}`;
  }
  return `${campo} ${op} ${cond.valor}`;
}

export function ruleStatusBadgeVariant(
  status: RetentionRuleStatus,
): 'info' | 'success' | 'warning' | 'error' | 'neutral' {
  return status === 'ATIVA' ? 'success' : 'neutral';
}

export function logStatusBadgeVariant(
  status: RetentionLogStatus,
): 'info' | 'success' | 'warning' | 'error' | 'neutral' {
  const map: Record<RetentionLogStatus, 'info' | 'success' | 'warning' | 'error' | 'neutral'> = {
    RETIDO: 'success',
    NAO_RETIDO: 'warning',
    ISENTO: 'neutral',
  };
  return map[status];
}

/* ── Hooks: Regras de retenção ── */

export function useRetentionRules(
  ctx?: ModuleCtx | null,
  params?: { page?: number; limit?: number; status?: RetentionRuleStatus; acao?: RetentionAction },
) {
  return useQuery({
    queryKey: ['retention', 'rules', ctx, params] as const,
    queryFn: async (): Promise<PaginatedResult<RetentionRule>> => {
      const res = await apiClient.get<PaginatedResult<RetentionRule>>('/retention/rules', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.acao ? { acao: params.acao } : {}),
        },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? params?.page ?? 1,
        limit: res.limit ?? params?.limit ?? 50,
        totalPages: res.totalPages ?? 1,
      };
    },
    enabled: !!ctx,
  });
}

export function useCreateRetentionRule(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RetentionRuleInput) =>
      apiClient.post<RetentionRule>(
        '/retention/rules',
        {
          prioridade: input.prioridade,
          condicao: input.condicao,
          acao: input.acao,
          descricao: input.descricao ?? null,
          retencaoNaFonte: input.retencaoNaFonte ?? true,
          autoLancamento: input.autoLancamento ?? false,
        },
        { params: ctx ? ctxParams(ctx) : {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'rules'] }).catch(() => {});
    },
  });
}

export function useUpdateRetentionRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: RetentionRuleInput & { id: string }) =>
      apiClient.patch<RetentionRule>(`/retention/rules/${id}`, {
        prioridade: input.prioridade,
        condicao: input.condicao,
        acao: input.acao,
        descricao: input.descricao ?? null,
        retencaoNaFonte: input.retencaoNaFonte,
        autoLancamento: input.autoLancamento,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'rules'] }).catch(() => {});
    },
  });
}

export function useDeleteRetentionRule(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/retention/rules/${id}`, {
        params: ctx ? ctxParams(ctx) : {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'rules'] }).catch(() => {});
    },
  });
}

/** POST /retention/rules/:id/activate | deactivate */
export function useToggleRetentionRule(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transition }: { id: string; transition: 'activate' | 'deactivate' }) =>
      apiClient.post<RetentionRule>(`/retention/rules/${id}/${transition}`, undefined, {
        params: ctx ? ctxParams(ctx) : {},
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'rules'] }).catch(() => {});
    },
  });
}

/* ── Hooks: Valores mínimos ── */

export function useRetentionMinimums(ctx?: ModuleCtx | null) {
  return useQuery({
    queryKey: ['retention', 'minimums', ctx] as const,
    queryFn: async (): Promise<PaginatedResult<RetentionMinimum>> => {
      const res = await apiClient.get<PaginatedResult<RetentionMinimum>>('/retention/minimums', {
        params: ctx ? { ...ctxParams(ctx), limit: '100' } : { limit: '100' },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? 1,
        limit: res.limit ?? 100,
        totalPages: res.totalPages ?? 1,
      };
    },
    enabled: !!ctx,
  });
}

export function useCreateRetentionMinimum(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RetentionMinimumInput) =>
      apiClient.post<RetentionMinimum>(
        '/retention/minimums',
        {
          tipoServico: input.tipoServico,
          valorMinimo: input.valorMinimo,
          vigenteDesde: input.vigenteDesde ?? undefined,
        },
        { params: ctx ? ctxParams(ctx) : {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'minimums'] }).catch(() => {});
    },
  });
}

export function useUpdateRetentionMinimum() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: RetentionMinimumInput & { id: string }) =>
      apiClient.patch<RetentionMinimum>(`/retention/minimums/${id}`, {
        tipoServico: input.tipoServico,
        valorMinimo: input.valorMinimo,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'minimums'] }).catch(() => {});
    },
  });
}

export function useDeleteRetentionMinimum(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/retention/minimums/${id}`, {
        params: ctx ? ctxParams(ctx) : {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'minimums'] }).catch(() => {});
    },
  });
}

/* ── Hooks: Exceções ── */

export function useRetentionExceptions(ctx?: ModuleCtx | null) {
  return useQuery({
    queryKey: ['retention', 'exceptions', ctx] as const,
    queryFn: async (): Promise<PaginatedResult<RetentionException>> => {
      const res = await apiClient.get<PaginatedResult<RetentionException>>('/retention/exceptions', {
        params: ctx ? { ...ctxParams(ctx), limit: '100' } : { limit: '100' },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? 1,
        limit: res.limit ?? 100,
        totalPages: res.totalPages ?? 1,
      };
    },
    enabled: !!ctx,
  });
}

export function useCreateRetentionException(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RetentionExceptionInput) =>
      apiClient.post<RetentionException>(
        '/retention/exceptions',
        {
          regraId: input.regraId ?? undefined,
          prestadorCnpj: input.prestadorCnpj,
          prestadorRazaoSocial: input.prestadorRazaoSocial ?? undefined,
          motivo: input.motivo,
          vigenciaInicio: input.vigenciaInicio,
          vigenciaFim: input.vigenciaFim ?? undefined,
        },
        { params: ctx ? ctxParams(ctx) : {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'exceptions'] }).catch(() => {});
    },
  });
}

export function useUpdateRetentionException() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: RetentionExceptionInput & { id: string }) =>
      apiClient.patch<RetentionException>(`/retention/exceptions/${id}`, {
        motivo: input.motivo,
        vigenciaInicio: input.vigenciaInicio,
        vigenciaFim: input.vigenciaFim ?? undefined,
        prestadorRazaoSocial: input.prestadorRazaoSocial ?? undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'exceptions'] }).catch(() => {});
    },
  });
}

export function useDeleteRetentionException(ctx?: ModuleCtx | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete<void>(`/retention/exceptions/${id}`, {
        params: ctx ? ctxParams(ctx) : {},
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retention', 'exceptions'] }).catch(() => {});
    },
  });
}

/* ── Hooks: Monitor + Audit log ── */

export function useRetentionMonitor(
  ctx?: ModuleCtx | null,
  params?: {
    page?: number;
    limit?: number;
    status?: RetentionLogStatus;
    prestadorCnpj?: string;
    dateFrom?: string;
    dateTo?: string;
  },
) {
  return useQuery({
    queryKey: ['retention', 'monitor', ctx, params] as const,
    queryFn: async (): Promise<RetentionMonitor> => {
      const res = await apiClient.get<RetentionMonitor>('/retention/monitor', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 20),
          ...(params?.status ? { status: params.status } : {}),
          ...(params?.prestadorCnpj ? { prestadorCnpj: params.prestadorCnpj } : {}),
          ...(params?.dateFrom ? { dateFrom: params.dateFrom } : {}),
          ...(params?.dateTo ? { dateTo: params.dateTo } : {}),
        },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? params?.page ?? 1,
        limit: res.limit ?? params?.limit ?? 20,
        totalPages: res.totalPages ?? 1,
        totais: res.totais ?? { valorBruto: 0, iss: 0, irrf: 0, retidos: 0, naoRetidos: 0, isentos: 0 },
      };
    },
    enabled: !!ctx,
  });
}

export function useRetentionAuditLog(
  ctx?: ModuleCtx | null,
  params?: { page?: number; limit?: number; origem?: RetentionOrigin; status?: RetentionLogStatus },
) {
  return useQuery({
    queryKey: ['retention', 'audit-log', ctx, params] as const,
    queryFn: async (): Promise<PaginatedResult<RetentionLog>> => {
      const res = await apiClient.get<PaginatedResult<RetentionLog>>('/retention/audit-log', {
        params: {
          ...(ctx ? ctxParams(ctx) : {}),
          page: String(params?.page ?? 1),
          limit: String(params?.limit ?? 50),
          ...(params?.origem ? { origem: params.origem } : {}),
          ...(params?.status ? { status: params.status } : {}),
        },
      });
      return {
        data: res.data ?? [],
        total: res.total ?? 0,
        page: res.page ?? params?.page ?? 1,
        limit: res.limit ?? params?.limit ?? 50,
        totalPages: res.totalPages ?? 1,
      };
    },
    enabled: !!ctx,
  });
}